import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { renderMaxLengthText } from 'utils/renderMaxLengthText';
import { isPlaceholderTeam } from 'containers/Mundial2026/utils/teamRowUtils';
import { useJugadoresStatColumnTooltip } from 'containers/Mundial2026/WCMatchDetails/WCJugadores/hooks/useJugadoresStatColumnTooltip';
import styles from './GroupTable.module.scss';

const MAX_HEADER_LABEL = 3;
const MAX_TEAM_NAME = 15;
const MAX_PLACEHOLDER_NAME = 20;
const MAX_STAT_VALUE = 3;
const MAX_RANK_VALUE = 3;

const isPlaceholder = row => !row.flag || row.code === 'PD';

// Convierte un color hex (#RRGGBB o #RGB) a rgba con la opacidad indicada.
// Devuelve null si el input no es un hex válido — el caller usa eso para
// decidir no aplicar highlight inline.
const hexToRgba = (hex, alpha = 0.2) => {
  if (!hex) return null;
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map(c => c + c)
          .join('')
      : clean;
  if (full.length !== 6) return null;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ShieldPlaceholder = () => (
  <svg
    className={styles['group-table__flag-placeholder']}
    xmlns="http://www.w3.org/2000/svg"
    width="11"
    height="14"
    viewBox="0 0 11 14"
    fill="none"
  >
    <path
      d="M5.33333 13.3333C3.78889 12.9444 2.51389 12.0583 1.50833 10.675C0.502778 9.29167 0 7.75556 0 6.06667V2L5.33333 0L10.6667 2V6.06667C10.6667 7.75556 10.1639 9.29167 9.15833 10.675C8.15278 12.0583 6.87778 12.9444 5.33333 13.3333ZM5.33333 11.9333C6.48889 11.5667 7.44444 10.8333 8.2 9.73333C8.95556 8.63333 9.33333 7.41111 9.33333 6.06667V2.91667L5.33333 1.41667L1.33333 2.91667V6.06667C1.33333 7.41111 1.71111 8.63333 2.46667 9.73333C3.22222 10.8333 4.17778 11.5667 5.33333 11.9333ZM5.825 9.79167C5.96389 9.65278 6.03333 9.48889 6.03333 9.3C6.03333 9.11111 5.96389 8.94722 5.825 8.80833C5.68611 8.66945 5.52222 8.6 5.33333 8.6C5.14444 8.6 4.98056 8.66945 4.84167 8.80833C4.70278 8.94722 4.63333 9.11111 4.63333 9.3C4.63333 9.48889 4.70278 9.65278 4.84167 9.79167C4.98056 9.93056 5.14444 10 5.33333 10C5.52222 10 5.68611 9.93056 5.825 9.79167ZM4.85 7.86667H5.85V7.5C5.85 7.37778 5.87778 7.26111 5.93333 7.15C6 6.99444 6.08889 6.86389 6.2 6.75833C6.31111 6.65278 6.42778 6.54444 6.55 6.43333C6.73889 6.24444 6.90278 6.03333 7.04167 5.8C7.18056 5.56667 7.25 5.31111 7.25 5.03333C7.25 4.53333 7.05833 4.125 6.675 3.80833C6.29167 3.49167 5.84444 3.33333 5.33333 3.33333C4.88889 3.33333 4.49167 3.46111 4.14167 3.71667C3.79167 3.97222 3.55556 4.31111 3.43333 4.73333L4.33333 5.1C4.4 4.87778 4.525 4.68889 4.70833 4.53333C4.89167 4.37778 5.1 4.3 5.33333 4.3C5.57778 4.3 5.79167 4.37222 5.975 4.51667C6.15833 4.66111 6.25 4.84444 6.25 5.06667C6.25 5.25556 6.19167 5.43056 6.075 5.59167C5.95833 5.75278 5.82778 5.9 5.68333 6.03333C5.55 6.15556 5.41667 6.28056 5.28333 6.40833C5.15 6.53611 5.04444 6.68333 4.96667 6.85C4.88889 7.00556 4.85 7.16945 4.85 7.34167V7.86667Z"
      fill="white"
      fill-opacity="0.25"
    />
  </svg>
);

const FlagImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return <ShieldPlaceholder />;
  }

  return (
    <img
      className={styles['group-table__flag']}
      src={src}
      alt={alt}
      onError={() => setHasError(true)}
    />
  );
};

const renderCell = (row, col, highlightStyle = null) => {
  if (col.key === 'rank') {
    const value = row.rank != null ? String(row.rank) : '-';
    return (
      <td
        key={col.key}
        className={`${styles['group-table__td']} ${
          styles['group-table__td--rank']
        }`}
        style={highlightStyle || undefined}
      >
        {renderMaxLengthText(value, MAX_RANK_VALUE)}
      </td>
    );
  }

  if (col.key === 'team.name') {
    const maxName = MAX_TEAM_NAME;
    const displayName = row.name || '';
    const displayCode = row.code || '';
    return (
      <td
        key={col.key}
        className={`${styles['group-table__td']} ${
          styles['group-table__td--country']
        }`}
        style={highlightStyle || undefined}
      >
        <div className={styles['group-table__country']}>
          <span className={styles['group-table__flag-wrapper']}>
            <FlagImage src={row.flag} alt={displayCode} />
          </span>
          <span
            className={`${styles['group-table__country-name']} ${
              styles['group-table__country-name--full']
            }`}
          >
            {renderMaxLengthText(displayName, maxName)}
          </span>
          <span
            className={`${styles['group-table__country-name']} ${
              styles['group-table__country-name--abbrev']
            }`}
          >
            {renderMaxLengthText(displayCode, maxName)}
          </span>
        </div>
      </td>
    );
  }

  const statKey = col.key.split('.')[1];
  const rawValue = row.stats[statKey];
  const value = rawValue != null ? String(rawValue) : '-';
  return (
    <td
      key={col.key}
      className={`${styles['group-table__td']} ${
        styles['group-table__td--stat']
      }`}
      style={highlightStyle || undefined}
    >
      {renderMaxLengthText(value, MAX_STAT_VALUE)}
    </td>
  );
};

const GroupTable = ({
  groupName,
  rows,
  fixedColumns,
  statsColumns,
  highlightTeamName,
  highlightColor,
}) => {
  const scrollRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [_searchParams, setSearchParams] = useSearchParams();
  const { bindHeaderTooltip, headerTooltipPortal } = useJugadoresStatColumnTooltip(scrollRef);
  // Calculado una sola vez por render. null si no hay color válido o no hay
  // teamName, en cuyo caso el highlightStyle por fila también queda en null.
  const highlightBg = hexToRgba(highlightColor, 0.2);

  const handleScroll = () => {
    setIsScrolled(scrollRef.current.scrollLeft > 0);
  };

  const handleRowClick = row => {
    if (isPlaceholderTeam(row)) return;
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('countryId', row.code);
      next.set('tab', 'countries');
      next.delete('screen');
      next.delete('subTab');
      return next;
    });
  };

  const handleRowKeyDown = (row, e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowClick(row);
    }
  };

  return (
    <div
      className={classNames(styles['group-table'], {
        [styles['group-table--scrolled']]: isScrolled,
      })}
    >
      {groupName && (
        <h2 className={styles['group-table__title']}>{groupName}</h2>
      )}
      <div className={styles['group-table__container']}>
        {/* Fixed left section */}
        <div className={styles['group-table__fixed']}>
          <table
            className={`${styles['group-table__table']} ${
              styles['group-table__table--fixed']
            }`}
          >
            <thead>
              <tr className={styles['group-table__header-row']}>
                {fixedColumns.map(col => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`${styles['group-table__th']} ${
                      styles[
                        `group-table__th--${
                          col.key === 'rank' ? 'rank' : 'country'
                        }`
                      ]
                    }`}
                  >
                    {renderMaxLengthText(
                      col.label || '',
                      col.key === 'team.name'
                        ? MAX_PLACEHOLDER_NAME
                        : MAX_HEADER_LABEL,
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const placeholder = isPlaceholderTeam(row);
                const isHighlighted =
                  highlightTeamName && row.name === highlightTeamName;
                const highlightStyle =
                  isHighlighted && highlightBg
                    ? { backgroundColor: highlightBg }
                    : null;
                return (
                  <tr
                    key={row.code}
                    className={classNames(styles['group-table__row'], {
                      [styles['group-table__row--interactive']]: !placeholder,
                    })}
                    {...!placeholder && {
                      onClick: () => handleRowClick(row),
                      onKeyDown: e => handleRowKeyDown(row, e),
                      role: 'button',
                      tabIndex: 0,
                      'aria-label': `Ver detalle de ${row.name}`,
                    }}
                  >
                    {fixedColumns.map(col =>
                      renderCell(row, col, highlightStyle),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Scrollable right section: stat columns */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className={styles['group-table__scrollable']}
        >
          <table
            className={`${styles['group-table__table']} ${
              styles['group-table__table--scrollable']
            }`}
          >
            <thead>
              <tr className={styles['group-table__header-row']}>
                {statsColumns.map(col => (
                  <th
                    key={col.key}
                    scope="col"
                    className={`${styles['group-table__th']} ${
                      styles['group-table__th--stat']
                    }`}
                    aria-label={col.tooltip ? `${col.label}: ${col.tooltip}` : col.label}
                    {...(col.tooltip ? bindHeaderTooltip(col.tooltip) : {})}
                  >
                    {renderMaxLengthText(col.label || '', MAX_HEADER_LABEL)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const placeholder = isPlaceholderTeam(row);
                const isHighlighted =
                  highlightTeamName && row.name === highlightTeamName;
                const highlightStyle =
                  isHighlighted && highlightBg
                    ? { backgroundColor: highlightBg }
                    : null;
                return (
                  <tr
                    key={row.code}
                    className={classNames(styles['group-table__row'], {
                      [styles['group-table__row--interactive']]: !placeholder,
                    })}
                    {...!placeholder && {
                      onClick: () => handleRowClick(row),
                    }}
                  >
                    {statsColumns.map(col =>
                      renderCell(row, col, highlightStyle),
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {headerTooltipPortal}
    </div>
  );
};

FlagImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
};

const columnShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string,
});

GroupTable.propTypes = {
  groupName: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      rank: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
      flag: PropTypes.string,
      stats: PropTypes.object.isRequired,
    }),
  ).isRequired,
  fixedColumns: PropTypes.arrayOf(columnShape).isRequired,
  statsColumns: PropTypes.arrayOf(columnShape).isRequired,
  highlightTeamName: PropTypes.string,
  highlightColor: PropTypes.string,
};

GroupTable.defaultProps = {
  highlightTeamName: null,
  highlightColor: null,
};

export default GroupTable;
